//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import React, { useCallback, useEffect, useState } from 'react';
import {
  FormProvider,
  useForm,
  useFormContext,
  useWatch,
} from 'react-hook-form';

import { SamplingCampaignRequest } from '@apis/campaign-requests/sampling/campaign-requests/samplingCampaignRequestsApiModel';
import {
  PurchaseOption,
  SamplingDeliveryMethod,
} from '@apis/campaign-requests/sampling/opportunities/samplingOpportunitiesApiModel';
import { SortDir } from '@apis/campaign-requests/shared/Sorting';
import { ChangePurchaseOptionConfirmation } from '@campaigns/components/campaign-requests/sampling-campaigns/edit/prepaid/modals/ChangePurchaseOptionModal/ChangePurchaseOptionConfirmation';
import {
  ChangePurchaseOptionModalProvider,
  ChangePurchaseOptionModalStep,
} from '@campaigns/components/campaign-requests/sampling-campaigns/edit/prepaid/modals/ChangePurchaseOptionModal/ChangePurchaseOptionModalContext';
import { ChangePurchaseOptionModalTable } from '@campaigns/components/campaign-requests/sampling-campaigns/edit/prepaid/modals/ChangePurchaseOptionModal/ChangePurchaseOptionModalTable';
import { CampaignModalContentSetup } from '@campaigns/components/campaigns-overview/create-campaign-modal/CampaignModalContentSetup';
import { ExpiredPurchaseOptionsTable } from '@campaigns/components/shared/ExpiredPurchaseOptionsTable';
import { usePurchaseOptions } from '@campaigns/components/shared/purchase-options/usePurchaseOptions';
import {
  PurchaseOptionsSortKey,
  UPDATE_EXPIRED_CHANGE_PURCHASE_OPTION_MODAL_TITLE,
  USER_ERROR_MESSAGES,
} from '@campaigns/constants/sampling/SamplingCampaignRequestConstants';
import { SamplingCampaignRequestSectionName } from '@campaigns/constants/sampling/SamplingCampaignRequestSectionConstants';
import { SAMPLING_CAMPAIGNS_TRACKING_ID } from '@campaigns/constants/sampling/SamplingCampaignsTrackingId';
import { useSamplingCampaign } from '@campaigns/hooks/useSamplingCampaign';
import { useSortAndPaginate } from '@campaigns/hooks/useSortAndPaginate';
import { isPurchaseOptionExpired } from '@campaigns/utils/sampling/SamplingCampaignRequestValidationRulesUtils';
import { CenteredSpinner } from '@components/atoms/spinner/Spinner';
import { Modal } from '@components/molecules/modal/Modal';
import { navigateToState } from '@react-bridge/wrappers/navigation';
import { pluralize } from '@utils/stringUtils';

interface ChangePurchaseOptionModalProps {
  headerText: string;
  isLoading?: boolean;
  isModalOpen: boolean;
  modalTitle: string;
  onClose: () => void;
  onCreateNewPurchase?: () => void;
  shouldIncludeTertiaryButton?: boolean;
}

export const ChangePurchaseOptionModal = ({
  headerText,
  isLoading = false,
  isModalOpen,
  modalTitle,
  onClose,
  onCreateNewPurchase,
  shouldIncludeTertiaryButton = false,
}: ChangePurchaseOptionModalProps) => {
  const mainFormMethods = useFormContext<SamplingCampaignRequest>();
  const localFormMethods = useForm<SamplingCampaignRequest>({
    defaultValues: mainFormMethods.getValues(),
    mode: 'onChange',
  });

  const {
    control,
    formState: { defaultValues },
    getValues,
    reset: resetLocalForm,
    setValue,
  } = localFormMethods;

  const { getValues: getMainValues, reset: resetMainForm } = mainFormMethods;

  useEffect(() => {
    if (isModalOpen) {
      resetLocalForm(getMainValues());
    }
  }, [getMainValues, isModalOpen, resetLocalForm]);

  const selectedOptions =
    useWatch({
      control,
      name: 'purchaseOption',
    }) || [];
  const selectedOptionId = selectedOptions[0]?.opportunityId;

  const { handleSave, isSaving } = useSamplingCampaign();

  const [step, setStep] = useState<ChangePurchaseOptionModalStep>(
    ChangePurchaseOptionModalStep.PURCHASE_OPTIONS,
  );
  const [submissionError, setSubmissionError] = useState<string | null>(null);

  const { error, isPending, purchaseOptions } = usePurchaseOptions({
    readOnly: false,
    selectedOptions: selectedOptions,
  });

  const {
    currentPage,
    itemsPerPage,
    processedItems,
    searchTerm,
    setCurrentPage,
    setItemsPerPage,
    setSearchTerm,
    setSort,
    sortDir = SortDir.ASC,
    sortKey = PurchaseOptionsSortKey.EXPIRY_DATE,
    totalItems,
  } = useSortAndPaginate<PurchaseOption, PurchaseOptionsSortKey>({
    initialSortDir: SortDir.ASC,
    initialSortKey: PurchaseOptionsSortKey.EXPIRY_DATE,
    items: purchaseOptions,
    searchKeys: [
      'opportunityRef',
      'attributes',
      PurchaseOptionsSortKey.EXPIRY_DATE,
    ],
  });

  const resultsText = pluralize(totalItems, 'option');

  const handleSortChange = (key: PurchaseOptionsSortKey) => {
    setSort(key, sortDir === SortDir.ASC ? SortDir.DESC : SortDir.ASC);
  };

  const handleSelectionChange = useCallback(
    (optionId: string) => {
      const option = purchaseOptions.find((p) => p.opportunityId === optionId);
      if (option) {
        setValue('purchaseOption', [option], {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        });
      }
    },
    [purchaseOptions, setValue],
  );

  const handleClose = () => {
    setStep(ChangePurchaseOptionModalStep.PURCHASE_OPTIONS);
    onClose();
  };

  const handleGoBackToCampaigns = () => {
    void navigateToState('campaigns.sampling');
  };

  const getConfirmationContext = () => {
    const selectedPurchaseOption = selectedOptions[0];
    const { fulfillmentType, whoShipsProduct } =
      getValues('fulfillmentOptions') || {};
    const targetDeliveryMethod = selectedPurchaseOption?.deliveryMethod;

    const isSourceShippedByBV =
      fulfillmentType === 'shipped' && whoShipsProduct === 'bazaarvoice';
    const isSourceDeals = fulfillmentType === 'deals';
    const isSourceShippedByYou =
      fulfillmentType === 'shipped' && whoShipsProduct === 'myCompany';

    const isTargetDigital =
      targetDeliveryMethod === SamplingDeliveryMethod.DigitalDelivery;
    const isTargetShippedByYou =
      targetDeliveryMethod === SamplingDeliveryMethod.Shipped;
    const isTargetShippedByBV =
      targetDeliveryMethod === SamplingDeliveryMethod.ShippedByBazaarvoice;

    interface TransitionRule {
      condition: boolean;
      removedSection?: SamplingCampaignRequestSectionName;
    }

    const transitionRules: TransitionRule[] = [
      {
        condition:
          isSourceShippedByBV && (isTargetDigital || isTargetShippedByYou),
        removedSection: SamplingCampaignRequestSectionName.PACKAGING,
      },
      {
        condition:
          isSourceDeals && (isTargetShippedByYou || isTargetShippedByBV),
        removedSection: SamplingCampaignRequestSectionName.FULFILLMENT,
      },
      {
        condition: isSourceShippedByYou && isTargetDigital,
        removedSection: SamplingCampaignRequestSectionName.FULFILLMENT,
      },
      {
        condition: isSourceShippedByYou && isTargetShippedByBV,
        removedSection: undefined,
      },
    ];

    const matchedRule = transitionRules.find(({ condition }) => condition);

    return {
      fulfillmentType,
      removedDataSection: matchedRule?.removedSection,
      shouldResetFulfillment: !!matchedRule,
      targetCampaignType: targetDeliveryMethod,
    };
  };

  const updateFormValuesForSetup = () => {
    const data = getValues();
    const selectedOption = data.purchaseOption?.[0];
    const savedOption = defaultValues?.purchaseOption?.[0];

    if (
      selectedOption &&
      selectedOption.opportunityId !== savedOption?.opportunityId
    ) {
      const currentFulfillmentOptions = getValues('fulfillmentOptions') || {};
      const { shouldResetFulfillment } = getConfirmationContext();
      const baseOptions = shouldResetFulfillment
        ? {}
        : currentFulfillmentOptions;

      if (selectedOption.deliveryMethod) {
        switch (selectedOption.deliveryMethod) {
          case SamplingDeliveryMethod.Shipped:
            setValue('fulfillmentOptions', {
              ...baseOptions,
              fulfillmentType: 'shipped',
              whoShipsProduct: 'myCompany',
            });
            break;
          case SamplingDeliveryMethod.ShippedByBazaarvoice:
            setValue('fulfillmentOptions', {
              ...baseOptions,
              fulfillmentType: 'shipped',
              whoShipsProduct: 'bazaarvoice',
            });
            break;
          case SamplingDeliveryMethod.DigitalDelivery:
            setValue('fulfillmentOptions', {
              ...baseOptions,
              fulfillmentType: 'deals',
            });
            break;
        }
      }
      setValue('isNewPurchase', false);
    }
  };

  const handleFormSubmit = async (data: SamplingCampaignRequest) => {
    try {
      setSubmissionError(null);
      resetMainForm(data);
      await handleSave(false);
      const campaignId = data.campaignRequestId;
      if (campaignId) {
        void navigateToState(
          'campaigns.sampling-prepaid-campaign-request-details-edit-products',
          {
            campaignId,
          },
        );
      }
      handleClose();
    } catch (e) {
      setSubmissionError(
        e instanceof Error ? e.message : USER_ERROR_MESSAGES.DEFAULT,
      );
    }
  };

  const isUpdateDisabled = !selectedOptionId;

  const handleNextOrSubmit = async () => {
    if (step === ChangePurchaseOptionModalStep.PURCHASE_OPTIONS) {
      if (selectedOptionId) {
        setStep(ChangePurchaseOptionModalStep.CONFIRM);
      }
    } else if (step === ChangePurchaseOptionModalStep.CONFIRM) {
      updateFormValuesForSetup();
      setStep(ChangePurchaseOptionModalStep.SETUP);
    } else {
      await localFormMethods.handleSubmit(handleFormSubmit)();
    }
  };

  const handleBack = () => {
    if (step === ChangePurchaseOptionModalStep.SETUP) {
      setStep(ChangePurchaseOptionModalStep.CONFIRM);
    } else if (step === ChangePurchaseOptionModalStep.CONFIRM) {
      setStep(ChangePurchaseOptionModalStep.PURCHASE_OPTIONS);
    } else {
      handleClose();
    }
  };

  const isPurchaseOptionsStep =
    step === ChangePurchaseOptionModalStep.PURCHASE_OPTIONS;
  const isConfirmStep = step === ChangePurchaseOptionModalStep.CONFIRM;

  const savedPurchaseOptionsValues = useWatch({
    control: mainFormMethods.control,
    name: 'purchaseOption',
  });

  const {
    isPending: isCheckingExpired,
    purchaseOptions: availableSavedOptions,
  } = usePurchaseOptions({
    isExpiredPurchaseOptionSelected: true,
    selectedOptions: savedPurchaseOptionsValues,
  });

  useEffect(() => {
    if (isCheckingExpired || savedPurchaseOptionsValues?.length === 0) {
      return;
    }

    const updatedOptions = savedPurchaseOptionsValues?.map((saved) => {
      if (isPurchaseOptionExpired(saved)) {
        const latestOption = availableSavedOptions.find(
          (opp) => opp.opportunityId === saved.opportunityId,
        );

        if (latestOption && !isPurchaseOptionExpired(latestOption)) {
          return latestOption;
        }
      }
      return saved;
    });

    const shouldUpdatePurchaseOption = updatedOptions?.some(
      (updated, index) => {
        const saved = savedPurchaseOptionsValues?.[index];
        return updated.expiryDate !== saved?.expiryDate;
      },
    );

    if (shouldUpdatePurchaseOption) {
      setValue('purchaseOption', updatedOptions);
    }
  }, [
    availableSavedOptions,
    isCheckingExpired,
    savedPurchaseOptionsValues,
    setValue,
  ]);

  const hasExpiredSelectedOption = savedPurchaseOptionsValues?.some((saved) => {
    const latestOption = availableSavedOptions.find(
      (opp) => opp.opportunityId === saved.opportunityId,
    );

    return isPurchaseOptionExpired(latestOption ?? saved);
  });

  const expiredOriginalOptions = savedPurchaseOptionsValues
    ?.map(
      (option) =>
        availableSavedOptions.find(
          (original) => original.opportunityId === option.opportunityId,
        ) ?? option,
    )
    .filter(isPurchaseOptionExpired);

  const currentModalTitle = isPurchaseOptionsStep
    ? hasExpiredSelectedOption
      ? UPDATE_EXPIRED_CHANGE_PURCHASE_OPTION_MODAL_TITLE
      : modalTitle
    : isConfirmStep
      ? 'Are You Sure?'
      : 'Update Campaign';

  const primaryButtonText = isPurchaseOptionsStep
    ? 'Next'
    : isConfirmStep
      ? 'Next'
      : 'Update Campaign';

  const renderContent = () => {
    switch (step) {
      case ChangePurchaseOptionModalStep.PURCHASE_OPTIONS:
        if (isCheckingExpired || isLoading) {
          return <CenteredSpinner />;
        }
        return (
          <>
            {hasExpiredSelectedOption && (
              <ExpiredPurchaseOptionsTable
                expiredSelectedOptions={expiredOriginalOptions ?? []}
              />
            )}
            <ChangePurchaseOptionModalTable
              currentPage={currentPage}
              error={error}
              expiredPurchaseOptionModalTitle={
                hasExpiredSelectedOption ? modalTitle : undefined
              }
              headerText={headerText}
              isLoading={isPending}
              itemsPerPage={itemsPerPage}
              onCreateNewPurchase={onCreateNewPurchase}
              onSelectionChange={handleSelectionChange}
              onSortChange={handleSortChange}
              processedItems={processedItems}
              resultsText={resultsText}
              searchTerm={searchTerm}
              selectedOptionId={selectedOptionId}
              setCurrentPage={setCurrentPage}
              setItemsPerPage={setItemsPerPage}
              setSearchTerm={setSearchTerm}
              showCreateNewPurchaseButton={!!onCreateNewPurchase}
              sortDir={sortDir}
              sortKey={sortKey}
              totalItems={totalItems}
            />
          </>
        );
      case ChangePurchaseOptionModalStep.CONFIRM: {
        const { fulfillmentType, removedDataSection, targetCampaignType } =
          getConfirmationContext();
        return (
          <ChangePurchaseOptionConfirmation
            fulfillmentType={fulfillmentType}
            removedDataSection={removedDataSection}
            targetCampaignType={targetCampaignType}
          />
        );
      }
      case ChangePurchaseOptionModalStep.SETUP:
        return <CampaignModalContentSetup />;
      default:
        throw new Error('Invalid modal step');
    }
  };

  return (
    <ChangePurchaseOptionModalProvider
      value={{
        formMethods: localFormMethods,
        handleFormSubmit,
        isPending: isSaving,
        setStep,
        step,
        submissionError,
      }}
    >
      <FormProvider {...localFormMethods}>
        <Modal
          closeModal={hasExpiredSelectedOption ? undefined : handleClose}
          contents={renderContent()}
          isModalOpen={isModalOpen}
          primaryButtonInfo={{
            buttonContent: primaryButtonText,
            isDisabled: isUpdateDisabled || isSaving || isLoading,
            onClick: handleNextOrSubmit,
            trackingId:
              SAMPLING_CAMPAIGNS_TRACKING_ID.CAMPAIGN_REQUEST_PURCHASE_OPTIONS_CHANGE_BUTTON,
          }}
          secondaryButtonInfo={
            !isPurchaseOptionsStep
              ? {
                  buttonContent: 'Back',
                  onClick: handleBack,
                  trackingId:
                    SAMPLING_CAMPAIGNS_TRACKING_ID.CAMPAIGN_REQUEST_PURCHASE_OPTIONS_CHANGE_CANCEL_BUTTON,
                }
              : undefined
          }
          shouldCloseOnClickOutside={false}
          shouldCloseOnEsc={false}
          shouldIncludeSecondaryButton={!isPurchaseOptionsStep}
          shouldIncludeTertiaryButton={
            shouldIncludeTertiaryButton || hasExpiredSelectedOption
          }
          tertiaryButtonInfo={
            hasExpiredSelectedOption
              ? {
                  buttonContent: 'Go back to Campaigns',
                  onClick: handleGoBackToCampaigns,
                  trackingId:
                    SAMPLING_CAMPAIGNS_TRACKING_ID.CAMPAIGN_BACK_BUTTON,
                }
              : undefined
          }
          title={currentModalTitle}
          width={isPurchaseOptionsStep ? 'extra-wide' : 'medium'}
        />
      </FormProvider>
    </ChangePurchaseOptionModalProvider>
  );
};
